import React, { useContext, memo } from 'react';
import { catPriceAdapter } from '@core-ui/price-case';
import {
  XLARGE,
  LARGE,
  MEDIUM,
  SMALL,
  BreakpointContext,
} from '@core-ui/breakpoint-provider';
import { MarketingContext } from '@core-ui/marketing-provider';
import { SubcategoryBanner } from '@core-ui/subcategory-banner';
import universal from '@core-ui/universal';
import Grid from '@cat/grid';
import { buildMarketingName } from '../collaborators/build-marketing-name';
import CategoryProductCard from '../../category-product-card';
import GridPlaceholder from './GridPlaceholder';
import GridBannerContainer from '../styled/GridBannerContainer';
import { WithInSortMarketing } from '../../in-sort-marketing';
import useComposedGrid from '../../../hooks/features/useComposedGrid';
import useBRRedesign from '../../../hooks/features/useBRRedesign';
import useQuickAdd from '../../../hooks/features/useQuickAdd';
import Autoscroll from '@cat/autoscroll';
import useSwitchContract from '../../../hooks/features/useSwitchContract';

const CoreUIProductGrid = universal(import('@core-ui/product-grid'), {
  key: 'default',
  loading: <GridPlaceholder />,
});

const ProductGrid = (props) => {
  const {
    criticalCss,
    productGroups,
    paginator,
    totalItemCount,
    selectedNodes = [],
  } = props;
  const { contentData, PageMarketing } = useContext(MarketingContext);
  const { size, greaterOrEqualTo } = useContext(BreakpointContext);
  const isComposedGridEnabled = useComposedGrid();
  const isBRRedesignEnabled = useBRRedesign();
  const quickAddEnabled = useQuickAdd();
  const isNewContract = useSwitchContract();

  const getGridColumns = () => {
    const xxLarge = isBRRedesignEnabled ? { 1441: 4 } : { 1280: 4 };
    return {
      [XLARGE]: 3,
      [LARGE]: 3,
      [MEDIUM]: 2,
      [SMALL]: 2,
      ...xxLarge,
    };
  };

  const getDivisionId = () => {
    const { id } =
      selectedNodes.find(({ type }) =>
        ['division', 'sub-division'].includes(type)
      ) || {};
    return id;
  };

  if (isComposedGridEnabled) {
    const gridColumns = getGridColumns();
    const divisionId = getDivisionId();
    return (
      <Autoscroll cid={props.cid}>
        {productGroups.map((productGroup, index) => (
          <>
            <GridBannerContainer isBRRedesignEnabled={isBRRedesignEnabled}>
              <SubcategoryBanner
                criticalCss={criticalCss}
                position="top"
                subcatCid={productGroup.cid}
                subcategoryName={productGroup.heading}
              />
            </GridBannerContainer>
            <WithInSortMarketing
              contentData={contentData}
              productData={productGroup.products}
              categoryId={props.cid}
              subCategoryId={productGroup.cid}
              divisionId={divisionId}
              numberOfProductsInARow={gridColumns[size]}
              isDesktop={greaterOrEqualTo(XLARGE)}
            >
              {(productDataWithISM) => (
                <Grid
                  key={index}
                  data={productDataWithISM}
                  index={index}
                  contents={{
                    default: (product) => (
                      <CategoryProductCard
                        productInfo={product}
                        isBRRedesignEnabled={isBRRedesignEnabled}
                        isQuickAddEnabled={quickAddEnabled}
                      />
                    ),
                    'in-sort': ({ pageMarketingComponentData }) => (
                      <PageMarketing
                        className="ism-container"
                        pageData={pageMarketingComponentData}
                        marketingName={buildMarketingName({
                          selectedNodes,
                          currentNode: productGroup.heading || '',
                        })}
                      />
                    ),
                  }}
                  columns={gridColumns}
                  spacing={isBRRedesignEnabled ? 0 : 0.5}
                  isBRRedesignEnabled={isBRRedesignEnabled}
                  {...props}
                />
              )}
            </WithInSortMarketing>
            <GridBannerContainer isBRRedesignEnabled={isBRRedesignEnabled}>
              <SubcategoryBanner
                criticalCss={criticalCss}
                position="bottom"
                subcatCid={productGroup.cid}
                subcategoryName={productGroup.heading}
              />
            </GridBannerContainer>
          </>
        ))}
      </Autoscroll>
    );
  } else {
    return (
      <CoreUIProductGrid
        pageIndex={paginator.pageNumberRequested}
        priceAdapter={catPriceAdapter}
        productGroups={productGroups}
        totalCount={totalItemCount}
        productCardComponent={CategoryProductCard}
        useQuickAddFeature={quickAddEnabled}
        {...props}
      />
    );
  }
};

export default memo(ProductGrid);
